import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Users, TrendingUp, Download, MousePointerClick, Plus, Trash2, BookOpen, Wrench, FileText, Calendar, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Loading } from "@/components/shared";
import OwnersTableAdmin from "@/pages/OwnersTableAdmin";
import DeskAdmin from "@/pages/DeskAdmin";
import SubscribersAdmin from "@/pages/SubscribersAdmin";
import SalesMasteryAdmin from "@/pages/SalesMasteryAdmin";
import { FunnelAdmin, PlaybooksAdmin, LeadMagnetAdmin, SequenceAdmin } from "@/pages/FunnelAdmin";
import { LibraryAdmin } from "@/pages/LibraryAdmin";
import { StackAdmin } from "@/pages/StackAdmin";
import { ApplicationsAdmin } from "@/pages/ApplicationsAdmin";
import { PodcastAdmin } from "@/pages/PodcastAdmin";
import { LearnAdmin } from "@/pages/LearnAdmin";
import { useBrand } from "@/context/BrandContext";
import { CATEGORIES, STAGES, FORMATS } from "@/lib/constants";
import api from "@/lib/api";

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-navy/5 text-navy"><Icon className="h-[18px] w-[18px]" strokeWidth={1.75} /></div>
        {sub && <span className="text-xs font-semibold text-emerald">{sub}</span>}
      </div>
      <p className="mt-3 font-heading text-2xl font-bold text-navy">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>;
}

export default function Admin() {
  const qc = useQueryClient();
  const invalidate = (...keys) => keys.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const { data: stats, isLoading } = useQuery({ queryKey: ["admin-analytics"], queryFn: async () => (await api.get("/admin/analytics")).data });
  const { data: config } = useQuery({ queryKey: ["public-config"], queryFn: async () => (await api.get("/config")).data });
  const ownersTableEnabled = config?.owners_table_enabled;

  const TABS = [["funnel", "Funnel"], ["applications", "Applications"], ["podcast", "Podcast"], ["learn", "Learn"], ["overview", "Overview"], ["playbooks", "Playbooks"], ["library", "Library"], ["stack", "Stack"], ["lead-magnet", "Lead Magnet"], ["sequence", "Sequence"], ["resources", "Resources"], ["courses", "Courses"], ["sales-mastery", "Sales Mastery"], ["tools", "Tools"], ["blog", "Blog"], ["desk", "The Desk"], ["subscribers", "Subscribers"], ["sessions", "Sessions"], ["requests", "Requests"], ["members", "Members"], ["brand", "Brand"]];
  if (ownersTableEnabled) TABS.splice(TABS.length - 1, 0, ["owners-table", "Owners' Table"]);

  return (
    <DashboardLayout>
      <div className="mb-8"><h1 className="font-heading text-3xl font-bold tracking-tight text-navy">Admin dashboard</h1><p className="mt-1 text-slate-600">Manage content, members and view platform analytics.</p></div>
      <Tabs defaultValue="funnel">
        <TabsList className="mb-6 flex w-full flex-wrap justify-start gap-1 bg-transparent p-0">
          {TABS.map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="rounded-md data-[state=active]:bg-navy data-[state=active]:text-white">{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview">
          {isLoading ? <Loading /> : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat icon={Users} label="Total members" value={stats.total_users} sub={`${stats.activation_rate}% activated`} />
                <Stat icon={TrendingUp} label="Paid members" value={stats.paid} sub={`${stats.conversion_rate}% conversion`} />
                <Stat icon={Download} label="Resource downloads" value={stats.downloads} />
                <Stat icon={MousePointerClick} label="Affiliate clicks" value={stats.affiliate_clicks} />
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-4">
                <Stat icon={FileText} label="Newsletter leads" value={stats.leads} />
                <Stat icon={BookOpen} label="Resources" value={stats.resources} />
                <Stat icon={GraduationCap} label="Courses" value={stats.courses} />
                <Stat icon={Wrench} label="Tools" value={stats.tools} />
              </div>
              <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
                <h3 className="font-heading font-semibold text-navy">Signups (last 7 days)</h3>
                <div className="mt-4 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.signups_series}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="day" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="signups" stroke="#059669" strokeWidth={2.5} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-white p-5"><p className="text-sm text-slate-500">Builder members</p><p className="mt-1 font-heading text-2xl font-bold text-electric">{stats.premium}</p></div>
                <div className="rounded-xl border border-slate-200 bg-white p-5"><p className="text-sm text-slate-500">Growth Pro members</p><p className="mt-1 font-heading text-2xl font-bold text-emerald">{stats.pro}</p></div>
                <div className="rounded-xl border border-slate-200 bg-white p-5"><p className="text-sm text-slate-500">Community posts</p><p className="mt-1 font-heading text-2xl font-bold text-navy">{stats.posts}</p></div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="funnel"><FunnelAdmin /></TabsContent>
        <TabsContent value="applications"><ApplicationsAdmin /></TabsContent>
        <TabsContent value="podcast"><PodcastAdmin /></TabsContent>
        <TabsContent value="learn"><LearnAdmin /></TabsContent>
        <TabsContent value="playbooks"><PlaybooksAdmin /></TabsContent>
        <TabsContent value="library"><LibraryAdmin /></TabsContent>
        <TabsContent value="stack"><StackAdmin /></TabsContent>
        <TabsContent value="lead-magnet"><LeadMagnetAdmin /></TabsContent>
        <TabsContent value="sequence"><SequenceAdmin /></TabsContent>
        <TabsContent value="resources"><ResourceAdmin invalidate={invalidate} /></TabsContent>
        <TabsContent value="courses"><CourseAdmin invalidate={invalidate} /></TabsContent>
        <TabsContent value="sales-mastery"><SalesMasteryAdmin /></TabsContent>
        <TabsContent value="tools"><ToolAdmin invalidate={invalidate} /></TabsContent>
        <TabsContent value="blog"><BlogAdmin invalidate={invalidate} /></TabsContent>
        <TabsContent value="desk"><DeskAdmin /></TabsContent>
        <TabsContent value="subscribers"><SubscribersAdmin /></TabsContent>
        <TabsContent value="sessions"><SessionAdmin invalidate={invalidate} /></TabsContent>
        <TabsContent value="requests"><RequestAdmin /></TabsContent>
        <TabsContent value="members"><MemberAdmin /></TabsContent>
        {ownersTableEnabled && <TabsContent value="owners-table"><OwnersTableAdmin /></TabsContent>}
        <TabsContent value="brand"><BrandAdmin /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function AdminList({ title, testid, columns, rows, onDelete, dialog }) {
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold text-navy">{title}</h2>
        {dialog}
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid={testid}>
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>{columns.map((c) => <th key={c} className="px-4 py-3">{c}</th>)}<th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.key} className="hover:bg-slate-50">
                {r.cells.map((cell, i) => <td key={i} className="px-4 py-3 text-slate-700">{cell}</td>)}
                <td className="px-4 py-3 text-right"><Button variant="ghost" size="sm" onClick={() => onDelete(r.id)} data-testid={`delete-${r.id}`} className="text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-slate-400">No items yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResourceAdmin({ invalidate }) {
  const { data } = useQuery({ queryKey: ["resources", {}], queryFn: async () => (await api.get("/resources")).data });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", short_description: "", full_description: "", cover_image: "", category: CATEGORIES[0], business_stage: "idea", resource_format: "template", membership_requirement: "free", featured: false });
  const create = async () => {
    if (!f.title) return toast.error("Title required");
    try { await api.post("/resources", f); toast.success("Resource created"); setOpen(false); invalidate("resources", "admin-analytics"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/resources/${id}`); toast.success("Deleted"); invalidate("resources", "admin-analytics"); } catch { toast.error("Failed"); } };
  return (
    <AdminList title="Resources" testid="admin-resources-table" columns={["Title", "Category", "Stage", "Tier"]}
      rows={(data || []).map((r) => ({ key: r.id, id: r.id, cells: [r.title, r.category, r.business_stage, r.membership_requirement] }))}
      onDelete={del}
      dialog={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="add-resource-btn"><Plus className="mr-1.5 h-4 w-4" /> Add resource</Button></DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New resource</DialogTitle></DialogHeader>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="res-title" /></Field></div>
              <div className="sm:col-span-2"><Field label="Short description"><Input value={f.short_description} onChange={(e) => setF({ ...f, short_description: e.target.value })} data-testid="res-short" /></Field></div>
              <div className="sm:col-span-2"><Field label="Full description"><Textarea rows={3} value={f.full_description} onChange={(e) => setF({ ...f, full_description: e.target.value })} data-testid="res-full" /></Field></div>
              <div className="sm:col-span-2"><Field label="Cover image URL"><Input value={f.cover_image} onChange={(e) => setF({ ...f, cover_image: e.target.value })} data-testid="res-cover" /></Field></div>
              <Field label="Category"><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="res-category"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Stage"><Select value={f.business_stage} onValueChange={(v) => setF({ ...f, business_stage: v })}><SelectTrigger data-testid="res-stage"><SelectValue /></SelectTrigger><SelectContent>{STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Format"><Select value={f.resource_format} onValueChange={(v) => setF({ ...f, resource_format: v })}><SelectTrigger data-testid="res-format"><SelectValue /></SelectTrigger><SelectContent>{FORMATS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Membership"><Select value={f.membership_requirement} onValueChange={(v) => setF({ ...f, membership_requirement: v })}><SelectTrigger data-testid="res-membership"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="free">Free</SelectItem><SelectItem value="premium">Builder</SelectItem><SelectItem value="pro">Growth Pro</SelectItem></SelectContent></Select></Field>
            </div>
            <DialogFooter><Button onClick={create} className="bg-emerald text-white hover:bg-emerald-700" data-testid="res-submit">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />
  );
}

function CourseAdmin({ invalidate }) {
  const { data } = useQuery({ queryKey: ["courses"], queryFn: async () => (await api.get("/courses")).data });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", subtitle: "", cover_image: "", membership_requirement: "pro" });
  const create = async () => {
    if (!f.title) return toast.error("Title required");
    try {
      await api.post("/courses", { ...f, modules: [{ title: "Module 1", lessons: [{ title: "Introduction", type: "video", content: "Welcome to the course." }] }] });
      toast.success("Course created"); setOpen(false); invalidate("courses", "admin-analytics");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/courses/${id}`); toast.success("Deleted"); invalidate("courses", "admin-analytics"); } catch { toast.error("Failed"); } };
  return (
    <AdminList title="Courses" testid="admin-courses-table" columns={["Title", "Lessons", "Tier"]}
      rows={(data || []).map((c) => ({ key: c.id, id: c.id, cells: [c.title, c.lesson_count, c.membership_requirement] }))}
      onDelete={del}
      dialog={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="add-course-btn"><Plus className="mr-1.5 h-4 w-4" /> Add course</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New course</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="course-title" /></Field>
              <Field label="Subtitle"><Input value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} data-testid="course-subtitle" /></Field>
              <Field label="Cover image URL"><Input value={f.cover_image} onChange={(e) => setF({ ...f, cover_image: e.target.value })} data-testid="course-cover" /></Field>
              <Field label="Membership"><Select value={f.membership_requirement} onValueChange={(v) => setF({ ...f, membership_requirement: v })}><SelectTrigger data-testid="course-membership"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="premium">Builder</SelectItem><SelectItem value="pro">Growth Pro</SelectItem></SelectContent></Select></Field>
              <p className="text-xs text-slate-500">A starter module and lesson are added automatically. Extend content via the API.</p>
            </div>
            <DialogFooter><Button onClick={create} className="bg-emerald text-white hover:bg-emerald-700" data-testid="course-submit">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />
  );
}

function ToolAdmin({ invalidate }) {
  const { data } = useQuery({ queryKey: ["tools", {}], queryFn: async () => (await api.get("/tools")).data });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", category: "AI", description: "", best_for: "", pricing_summary: "", recommendation: "", affiliate_link: "", featured: false });
  const create = async () => {
    if (!f.name) return toast.error("Name required");
    try { await api.post("/tools", f); toast.success("Tool added"); setOpen(false); invalidate("tools", "admin-analytics"); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/tools/${id}`); toast.success("Deleted"); invalidate("tools", "admin-analytics"); } catch { toast.error("Failed"); } };
  return (
    <AdminList title="Tools" testid="admin-tools-table" columns={["Name", "Category", "Clicks"]}
      rows={(data || []).map((t) => ({ key: t.id, id: t.id, cells: [t.name, t.category, t.click_count || 0] }))}
      onDelete={del}
      dialog={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="add-tool-btn"><Plus className="mr-1.5 h-4 w-4" /> Add tool</Button></DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New tool</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="tool-name" /></Field>
              <Field label="Category"><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="tool-cat" /></Field>
              <Field label="Description"><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="tool-desc" /></Field>
              <Field label="Best for"><Input value={f.best_for} onChange={(e) => setF({ ...f, best_for: e.target.value })} data-testid="tool-bestfor" /></Field>
              <Field label="Pricing summary"><Input value={f.pricing_summary} onChange={(e) => setF({ ...f, pricing_summary: e.target.value })} data-testid="tool-pricing" /></Field>
              <Field label="Recommendation"><Textarea rows={2} value={f.recommendation} onChange={(e) => setF({ ...f, recommendation: e.target.value })} data-testid="tool-rec" /></Field>
              <Field label="Affiliate link"><Input value={f.affiliate_link} onChange={(e) => setF({ ...f, affiliate_link: e.target.value })} data-testid="tool-affiliate" /></Field>
            </div>
            <DialogFooter><Button onClick={create} className="bg-emerald text-white hover:bg-emerald-700" data-testid="tool-submit">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />
  );
}

function BlogAdmin({ invalidate }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-blog"], queryFn: async () => (await api.get("/admin/blog")).data });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", excerpt: "", content: "", cover_image: "", tags: "" });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-blog"] }); qc.invalidateQueries({ queryKey: ["blog"] }); qc.invalidateQueries({ queryKey: ["blog-facets"] }); };
  const create = async () => {
    if (!f.title) return toast.error("Title required");
    try { await api.post("/blog", { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) }); toast.success("Published"); setOpen(false); refresh(); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete permanently?")) return; try { await api.delete(`/blog/${id}`); toast.success("Deleted"); refresh(); } catch { toast.error("Failed"); } };
  const archive = async (id) => { try { await api.post(`/admin/blog/${id}/archive`); toast.success("Archived"); refresh(); } catch { toast.error("Failed"); } };
  const restore = async (id) => { try { await api.post(`/admin/blog/${id}/restore`); toast.success("Restored"); refresh(); } catch { toast.error("Failed"); } };
  const rows = data || [];
  const badge = (s, pub) => {
    const st = s || (pub ? "published" : "archived");
    const map = { published: "bg-emerald/10 text-emerald", archived: "bg-slate-100 text-slate-500", queued: "bg-blue-50 text-electric", held: "bg-orange-50 text-orange-600", rejected: "bg-red-50 text-red-600" };
    return <span className={`rounded px-2 py-0.5 text-xs font-bold uppercase ${map[st] || "bg-slate-100 text-slate-500"}`}>{st}</span>;
  };
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold text-navy">Blog posts</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="add-blog-btn"><Plus className="mr-1.5 h-4 w-4" /> New post</Button></DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New blog post</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="blog-title" /></Field>
              <Field label="Excerpt"><Input value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} data-testid="blog-excerpt" /></Field>
              <Field label="Content (markdown supported)"><Textarea rows={5} value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} data-testid="blog-content" /></Field>
              <Field label="Cover image URL"><Input value={f.cover_image} onChange={(e) => setF({ ...f, cover_image: e.target.value })} data-testid="blog-cover" /></Field>
              <Field label="Tags (comma separated)"><Input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} data-testid="blog-tags" /></Field>
            </div>
            <DialogFooter><Button onClick={create} className="bg-emerald text-white hover:bg-emerald-700" data-testid="blog-submit">Publish</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="admin-blog-table">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Author</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((p) => {
              const st = p.status || (p.published ? "published" : "archived");
              return (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-navy">{p.title}{p.is_desk && <span className="ml-2 rounded bg-emerald/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald">Desk</span>}</td>
                  <td className="px-4 py-3 text-slate-600">{p.author}</td>
                  <td className="px-4 py-3">{badge(p.status, p.published)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      {st === "archived" ? (
                        <Button variant="ghost" size="sm" onClick={() => restore(p.id)} data-testid={`restore-${p.id}`} className="text-emerald hover:bg-emerald/5">Restore</Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => archive(p.id)} data-testid={`archive-${p.id}`} className="text-slate-500 hover:bg-slate-100">Archive</Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => del(p.id)} data-testid={`delete-${p.id}`} className="text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No posts yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WorkshopAdmin({ invalidate }) {
  const { data } = useQuery({ queryKey: ["workshops"], queryFn: async () => (await api.get("/workshops")).data });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", scheduled_at: "", membership_requirement: "pro" });
  const create = async () => {
    if (!f.title || !f.scheduled_at) return toast.error("Title and date required");
    try { await api.post("/workshops", { ...f, scheduled_at: new Date(f.scheduled_at).toISOString() }); toast.success("Workshop created"); setOpen(false); invalidate("workshops"); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/workshops/${id}`); toast.success("Deleted"); invalidate("workshops"); } catch { toast.error("Failed"); } };
  return (
    <AdminList title="Workshops" testid="admin-workshops-table" columns={["Title", "When", "Tier"]}
      rows={(data || []).map((w) => ({ key: w.id, id: w.id, cells: [w.title, new Date(w.scheduled_at).toLocaleDateString(), w.membership_requirement] }))}
      onDelete={del}
      dialog={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="add-workshop-btn"><Plus className="mr-1.5 h-4 w-4" /> Add workshop</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New workshop</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="ws-title" /></Field>
              <Field label="Description"><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="ws-desc" /></Field>
              <Field label="Date & time"><Input type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} data-testid="ws-date" /></Field>
              <Field label="Membership"><Select value={f.membership_requirement} onValueChange={(v) => setF({ ...f, membership_requirement: v })}><SelectTrigger data-testid="ws-membership"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="premium">Builder</SelectItem><SelectItem value="pro">Growth Pro</SelectItem></SelectContent></Select></Field>
            </div>
            <DialogFooter><Button onClick={create} className="bg-emerald text-white hover:bg-emerald-700" data-testid="ws-submit">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />
  );
}

function MemberAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-members"], queryFn: async () => (await api.get("/admin/members")).data });
  const setTier = async (id, tier) => {
    try { await api.put(`/admin/members/${id}/tier`, { tier }); toast.success("Membership updated"); qc.invalidateQueries({ queryKey: ["admin-members"] }); qc.invalidateQueries({ queryKey: ["admin-analytics"] }); }
    catch { toast.error("Failed"); }
  };
  return (
    <div>
      <h2 className="mb-4 font-heading text-xl font-semibold text-navy">Members</h2>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="admin-members-table">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Stage</th><th className="px-4 py-3">Membership</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(data || []).map((m) => (
              <tr key={m.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-navy">{m.name}</td>
                <td className="px-4 py-3 text-slate-600">{m.email}</td>
                <td className="px-4 py-3 text-slate-600">{m.profile?.stage || "—"}</td>
                <td className="px-4 py-3">
                  <Select value={m.tier} onValueChange={(v) => setTier(m.id, v)}>
                    <SelectTrigger className="h-9 w-[140px]" data-testid={`member-tier-${m.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="free">Free</SelectItem><SelectItem value="premium">Builder</SelectItem><SelectItem value="pro">Growth Pro</SelectItem></SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
            {(!data || data.length === 0) && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">No members yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BrandAdmin() {
  const { brand, reloadBrand } = useBrand();
  const [f, setF] = useState(brand);
  React.useEffect(() => { setF(brand); }, [brand]);
  const save = async () => {
    try {
      await api.put("/admin/brand", {
        name: f.name, tagline: f.tagline, description: f.description, logo_text: f.logo_text,
        logo_url: f.logo_url, favicon_url: f.favicon_url, accent_color: f.accent_color,
        footer_note: f.footer_note, social: f.social || {},
      });
      await reloadBrand();
      toast.success("Brand settings saved");
    } catch { toast.error("Could not save"); }
  };
  const s = f.social || {};
  return (
    <div className="max-w-2xl">
      <h2 className="mb-4 font-heading text-xl font-semibold text-navy">Brand settings</h2>
      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-brand-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Platform name"><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="brand-name" /></Field>
          <Field label="Logo text"><Input value={f.logo_text || ""} onChange={(e) => setF({ ...f, logo_text: e.target.value })} data-testid="brand-logo-text" /></Field>
        </div>
        <Field label="Tagline"><Input value={f.tagline || ""} onChange={(e) => setF({ ...f, tagline: e.target.value })} data-testid="brand-tagline" /></Field>
        <Field label="Description"><Textarea rows={2} value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="brand-description" /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Accent colour (hex)"><Input value={f.accent_color || ""} onChange={(e) => setF({ ...f, accent_color: e.target.value })} data-testid="brand-accent" /></Field>
          <Field label="Favicon URL"><Input value={f.favicon_url || ""} onChange={(e) => setF({ ...f, favicon_url: e.target.value })} data-testid="brand-favicon" /></Field>
        </div>
        <Field label="Logo image URL"><Input value={f.logo_url || ""} onChange={(e) => setF({ ...f, logo_url: e.target.value })} data-testid="brand-logo-url" /></Field>
        <Field label="Footer note"><Input value={f.footer_note || ""} onChange={(e) => setF({ ...f, footer_note: e.target.value })} data-testid="brand-footer" /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Twitter/X URL"><Input value={s.twitter || ""} onChange={(e) => setF({ ...f, social: { ...s, twitter: e.target.value } })} data-testid="brand-twitter" /></Field>
          <Field label="LinkedIn URL"><Input value={s.linkedin || ""} onChange={(e) => setF({ ...f, social: { ...s, linkedin: e.target.value } })} data-testid="brand-linkedin" /></Field>
          <Field label="YouTube URL"><Input value={s.youtube || ""} onChange={(e) => setF({ ...f, social: { ...s, youtube: e.target.value } })} data-testid="brand-youtube" /></Field>
          <Field label="Instagram URL"><Input value={s.instagram || ""} onChange={(e) => setF({ ...f, social: { ...s, instagram: e.target.value } })} data-testid="brand-instagram" /></Field>
        </div>
        <Button onClick={save} className="rounded-md bg-navy text-white hover:bg-navysurface" data-testid="brand-save">Save brand settings</Button>
      </div>
    </div>
  );
}

function SessionAdmin({ invalidate }) {
  const { data } = useQuery({ queryKey: ["sessions"], queryFn: async () => (await api.get("/sessions")).data });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", description: "", scheduled_at: "", membership_requirement: "pro", meeting_link: "https://meet.google.com/placeholder" });
  const create = async () => {
    if (!f.title || !f.scheduled_at) return toast.error("Title and date required");
    try { await api.post("/sessions", { ...f, scheduled_at: new Date(f.scheduled_at).toISOString() }); toast.success("Session scheduled"); setOpen(false); invalidate("sessions"); }
    catch { toast.error("Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/sessions/${id}`); toast.success("Deleted"); invalidate("sessions"); } catch { toast.error("Failed"); } };
  return (
    <AdminList title="Live sessions" testid="admin-sessions-table" columns={["Title", "When", "Tier", "Registered"]}
      rows={(data || []).map((w) => ({ key: w.id, id: w.id, cells: [w.title, new Date(w.scheduled_at).toLocaleDateString(), w.membership_requirement, w.registrant_count] }))}
      onDelete={del}
      dialog={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="add-session-btn"><Plus className="mr-1.5 h-4 w-4" /> Schedule session</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New live session</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="sess-title" /></Field>
              <Field label="Description"><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="sess-desc" /></Field>
              <Field label="Date & time"><Input type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} data-testid="sess-date" /></Field>
              <Field label="Meeting link"><Input value={f.meeting_link} onChange={(e) => setF({ ...f, meeting_link: e.target.value })} data-testid="sess-link" /></Field>
              <Field label="Membership"><Select value={f.membership_requirement} onValueChange={(v) => setF({ ...f, membership_requirement: v })}><SelectTrigger data-testid="sess-membership"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="premium">Premium</SelectItem><SelectItem value="pro">Pro</SelectItem></SelectContent></Select></Field>
            </div>
            <DialogFooter><Button onClick={create} className="bg-emerald text-white hover:bg-emerald-700" data-testid="sess-submit">Schedule</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />
  );
}

function RequestAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-requests"], queryFn: async () => (await api.get("/requests")).data });
  const STATUSES = ["Submitted", "Under Review", "Planned", "In Production", "Published", "Declined"];
  const setStatus = async (id, status) => {
    try { await api.put(`/requests/${id}/status`, { status, admin_response: "" }); toast.success("Status updated"); qc.invalidateQueries({ queryKey: ["admin-requests"] }); }
    catch { toast.error("Failed"); }
  };
  return (
    <div>
      <h2 className="mb-4 font-heading text-xl font-semibold text-navy">Resource requests</h2>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="admin-requests-table">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Votes</th><th className="px-4 py-3">Status</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(data || []).map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-navy">{r.title}</td>
                <td className="px-4 py-3 text-slate-600">{r.vote_count}</td>
                <td className="px-4 py-3">
                  <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                    <SelectTrigger className="h-9 w-[160px]" data-testid={`request-status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
            {(!data || data.length === 0) && <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-400">No requests yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
